"use client";

import { useEffect, useRef, useState } from "react";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { ReviewCard } from "@/components/reviews/review-card";
import { resolveBg } from "@/lib/bg-color";
import type { GoogleReview } from "@/lib/google-reviews";

export type ReviewsGridBlok = SbBlokData & {
  title?: string;
  description?: string;
  view_more_label?: string;
  bg_color?: unknown;
};

// Collapsed masonry height — taller on desktop. Keep these in sync with the
// `max-h-*` classes on the clip wrapper below; the lg breakpoint is 1115px.
const COLLAPSED_MAX_H_MOBILE = 1000;
const COLLAPSED_MAX_H_DESKTOP = 1700;

export default function ReviewsGridView({
  blok,
  reviews,
}: {
  blok: ReviewsGridBlok;
  reviews: GoogleReview[];
}) {
  const { bg, onDark, hex } = resolveBg(blok.bg_color, "white");
  const [expanded, setExpanded] = useState(false);
  // Assume it overflows so the first paint is already clipped (no flash); the
  // effect corrects it down for the rare case of very few reviews.
  const [overflows, setOverflows] = useState(true);
  const gridRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const check = () => {
      const limit = window.innerWidth >= 1115 ? COLLAPSED_MAX_H_DESKTOP : COLLAPSED_MAX_H_MOBILE;
      setOverflows(el.offsetHeight > limit + 8);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    window.addEventListener("resize", check);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [reviews.length]);

  if (reviews.length === 0) return null;

  const collapsed = !expanded && overflows;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,4.5rem)]">
        <div className="flex flex-col gap-4">
          {blok.title && (
            <h2
              className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
                onDark ? "text-white" : "text-purple-05"
              }`}
            >
              {blok.title}
            </h2>
          )}
          {blok.description && (
            <p
              className={`text-body max-w-[640px] leading-[1.6] ${
                onDark ? "text-white/85" : "text-black/65"
              }`}
            >
              {blok.description}
            </p>
          )}
        </div>

        <div className="relative mt-8 lg:mt-10">
          <div className={collapsed ? "max-h-[1000px] overflow-hidden lg:max-h-[1700px]" : ""}>
            <ul ref={gridRef} className="columns-1 gap-5 sm:columns-2 [@media(min-width:1024px)]:columns-3">
              {reviews.map((review, i) => (
                <li key={i} className="mb-5 break-inside-avoid">
                  <ReviewCard review={review} fullText />
                </li>
              ))}
            </ul>
          </div>
          {collapsed && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-48"
              style={{
                background: `linear-gradient(to top, ${hex}, transparent)`,
              }}
            />
          )}
        </div>

        {collapsed && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className={`text-body inline-flex cursor-pointer items-center gap-1.5 font-bold hover:underline ${
                onDark ? "text-white" : "text-purple-03"
              }`}
            >
              {blok.view_more_label || "View more"}
              <span aria-hidden className="text-body leading-none">
                +
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
